export const useWellSpringBilling = () => {
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Credit conversion constants - make this explicit and consistent
  const CREDITS_PER_DOLLAR = 10  // $1 AUD = 10 credits
  const DOLLARS_PER_CREDIT = 0.10 // 1 credit = $0.10 AUD

  // Convert between credits and dollars
  const creditsToDollars = (credits: number): number => credits * DOLLARS_PER_CREDIT
  const dollarsToCredits = (dollars: number): number => dollars * CREDITS_PER_DOLLAR

  // Record credit consumption for advice sessions
  const recordWellSpringAdvice = async (creditsUsed: number, adviceMetadata: Record<string, any> = {}) => {
    loading.value = true
    error.value = null

    try {
      // Convert credits to dollar amount for consumption
      const dollarAmount = creditsToDollars(creditsUsed)

      // Use budget-capped consumption for WellSpring advice
      console.log('🌱 Recording WellSpring advice via budget-capped consumption:', {
        credits: creditsUsed,
        dollars: dollarAmount,
        advice_type: adviceMetadata.advice_type
      })

      const consumptionResult = await $fetch('/api/billing/consume-credits-budget-capped', {
        method: 'POST',
        body: {
          amount: dollarAmount,
          description: `WellSpring Advice - ${creditsUsed} credits (${adviceMetadata.advice_type || 'unknown'})`,
          metadata: {
            marketplace: 'wellspring',
            advice_type: adviceMetadata.advice_type,
            advisor_id: adviceMetadata.advisor_id,
            session_id: adviceMetadata.session_id,
            session_duration: adviceMetadata.session_duration,
            credits_cost: creditsUsed,
            dollar_equivalent: dollarAmount,
            consumption_type: 'wellspring_advice',
            ...adviceMetadata
          }
        }
      }) as any

      if (!consumptionResult || !consumptionResult.success) {
        // Handle budget limit exceeded specifically
        if (consumptionResult?.blocked && consumptionResult?.error_code === 'budget_limit_exceeded') {
          error.value = `Budget limit exceeded. ${consumptionResult.message}`
          return {
            success: false,
            error: 'budget_limit_exceeded',
            message: consumptionResult.message,
            budget_details: consumptionResult.budget_details,
            action_required: 'increase_budget_or_reduce_usage'
          }
        }
        
        error.value = consumptionResult?.message || 'Failed to consume credits for advice session'
        return consumptionResult || { success: false, error: error.value }
      }

      console.log('✅ WellSpring advice recorded successfully:', {
        meter_event_id: consumptionResult.usage_tracking?.meter_event_id,
        credits_consumed: creditsUsed,
        budget_utilization: consumptionResult.budget_status?.budget_utilization
      })

      return {
        success: true,
        credits_consumed: creditsUsed,
        dollar_amount: dollarAmount,
        meter_event_id: consumptionResult.usage_tracking?.meter_event_id,
        budget_status: consumptionResult.budget_status,
        billing_model: 'budget_capped_postpaid'
      }

    } catch (err) {
      console.error('Error recording WellSpring credit usage:', err)
      error.value = err instanceof Error ? err.message : 'Failed to record credit usage'
      return false
    } finally {
      loading.value = false
    }
  }

  // Grant promotional credits for WellSpring
  const grantWellSpringCredits = async (
    credits: number, 
    options: {
      category?: 'promotional' | 'prepaid'
      name?: string
      expires_at?: number
      effective_at?: number
      metadata?: Record<string, any>
    } = {}
  ) => {
    loading.value = true
    error.value = null

    try {
      // Convert credits to dollar amount using our explicit conversion
      const dollarAmount = creditsToDollars(credits)

      const response = await $fetch('/api/billing/grant-credits', {
        method: 'POST',
        body: {
          amount: dollarAmount,
          currency: 'aud',
          category: options.category || 'promotional',
          name: options.name || `WellSpring Credits - ${credits} credits ($${dollarAmount})`,
          expires_at: options.expires_at,
          effective_at: options.effective_at,
          metadata: {
            marketplace: 'wellspring',
            credits_granted: credits.toString(),
            dollar_value: dollarAmount.toString(),
            credits_per_dollar: CREDITS_PER_DOLLAR.toString(),
            ...options.metadata
          }
        }
      }) as any

      if (!response?.success) {
        error.value = response?.error || 'Failed to grant credits'
        return false
      }

      return response.credit_grant
    } catch (err) {
      console.error('Error granting WellSpring credits:', err)
      error.value = err instanceof Error ? err.message : 'Failed to grant credits'
      return false
    } finally {
      loading.value = false
    }
  }

  // Calculate advice pricing (this would typically come from your pricing engine)
  const calculateAdvicePrice = (
    advisorTier: 'basic' | 'premium' | 'expert',
    sessionDuration: number, // in minutes
    adviceType: 'text' | 'audio' | 'video' | 'live'
  ): number => {
    // Base rates per minute in credits
    const baseTierRates = {
      basic: 2,    // 2 credits per minute
      premium: 4,  // 4 credits per minute
      expert: 8    // 8 credits per minute
    }

    // Multipliers for advice type
    const typeMultipliers = {
      text: 1.0,
      audio: 1.2,
      video: 1.5,
      live: 2.0
    }

    const baseRate = baseTierRates[advisorTier] || baseTierRates.basic
    const typeMultiplier = typeMultipliers[adviceType] || 1.0
    
    // Minimum 5 credits, maximum 500 credits per session
    const totalCredits = Math.max(5, Math.min(500, Math.ceil(sessionDuration * baseRate * typeMultiplier)))
    
    return totalCredits
  }

  // Get pricing breakdown for advice
  const getAdvicePricing = (
    advisorTier: 'basic' | 'premium' | 'expert',
    sessionDuration: number,
    adviceType: 'text' | 'audio' | 'video' | 'live'
  ) => {
    const credits = calculateAdvicePrice(advisorTier, sessionDuration, adviceType)
    const dollars = creditsToDollars(credits)
    
    return {
      credits,
      dollars,
      formatted: `${credits} credits ($${dollars.toFixed(2)} AUD)`
    }
  }

  // Get credit balance in credits (not dollars)
  const getCreditBalance = () => {
    const { credits } = useCredits()
    // Convert dollar balance to credits using our explicit conversion
    return computed(() => Math.floor(dollarsToCredits(credits.value)))
  }

  // Get credit balance in both credits and dollars
  const getCreditBalanceDetailed = () => {
    const { credits } = useCredits()
    return computed(() => {
      const dollarBalance = credits.value
      const creditBalance = Math.floor(dollarsToCredits(dollarBalance))
      return {
        credits: creditBalance,
        dollars: dollarBalance,
        formatted: `${creditBalance} credits ($${dollarBalance.toFixed(2)} AUD)`
      }
    })
  }

  // Check if user has enough credits for advice session
  const canAffordAdvice = (requiredCredits: number) => {
    const creditBalance = getCreditBalance()
    return computed(() => creditBalance.value >= requiredCredits)
  }

  // Purchase credits using existing credit purchase system
  const purchaseWellSpringCredits = async (credits: number) => {
    const { purchaseCredits } = useCredits()
    // Convert credits to dollar amount using our explicit conversion
    const dollarAmount = creditsToDollars(credits)
    return await purchaseCredits(dollarAmount)
  }

  // Purchase credits by dollar amount (for the UI that shows dollar amounts)
  const purchaseWellSpringCreditsByDollars = async (dollars: number) => {
    const { purchaseCredits } = useCredits()
    return await purchaseCredits(dollars)
  }

  // Convenience methods for different advice types
  const recordTextAdvice = (creditsUsed: number, advisorId: string, sessionId: string) => {
    return recordWellSpringAdvice(creditsUsed, {
      advice_type: 'text',
      advisor_id: advisorId,
      session_id: sessionId
    })
  }

  const recordVideoAdvice = (creditsUsed: number, advisorId: string, sessionId: string, duration: number) => {
    return recordWellSpringAdvice(creditsUsed, {
      advice_type: 'video',
      advisor_id: advisorId,
      session_id: sessionId,
      session_duration: duration
    })
  }

  const recordLiveAdvice = (creditsUsed: number, advisorId: string, sessionId: string, duration: number) => {
    return recordWellSpringAdvice(creditsUsed, {
      advice_type: 'live',
      advisor_id: advisorId,
      session_id: sessionId,
      session_duration: duration
    })
  }

  // Grant welcome credits for new WellSpring users
  const grantWelcomeCredits = (credits: number = 50, expiresInDays?: number) => {
    const expires_at = expiresInDays 
      ? Math.floor(Date.now() / 1000) + (expiresInDays * 24 * 60 * 60)
      : undefined

    return grantWellSpringCredits(credits, {
      category: 'promotional',
      name: `WellSpring Welcome Bonus - ${credits} credits ($${creditsToDollars(credits)})`,
      expires_at,
      metadata: {
        source: 'welcome_bonus',
        expires_in_days: expiresInDays?.toString()
      }
    })
  }

  return {
    loading: readonly(loading),
    error: readonly(error),
    
    // Constants and conversion utilities
    CREDITS_PER_DOLLAR,
    DOLLARS_PER_CREDIT,
    creditsToDollars,
    dollarsToCredits,
    
    // Core WellSpring functions
    recordWellSpringAdvice,
    grantWellSpringCredits,
    purchaseWellSpringCredits,
    purchaseWellSpringCreditsByDollars,
    
    // Pricing and balance utilities
    calculateAdvicePrice,
    getAdvicePricing,
    getCreditBalance,
    getCreditBalanceDetailed,
    canAffordAdvice,
    
    // Convenience methods for different advice types
    recordTextAdvice,
    recordVideoAdvice,
    recordLiveAdvice,
    
    // Welcome bonus
    grantWelcomeCredits
  }
} 